// src/services/trombinosServices.js
import axios from "axios";

const baseUrl = `${process.env.REACT_APP_API_URL}/api/trombinos`;

/* =========================
   Helpers
   ========================= */

// Lit le token à chaque appel (utile après login/logout)
const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Construit une querystring propre
const buildQuery = (params = {}) => {
  const q = new URLSearchParams();

  Object.entries(params).forEach(([key, val]) => {
    if (val === undefined || val === null) return;

    // Tableaux -> "a,b,c"
    if (Array.isArray(val)) {
      const joined = val
        .map((x) => String(x).trim())
        .filter(Boolean)
        .join(",");
      if (joined) q.append(key, joined);
      return;
    }

    // Booléens -> "true"/"false"
    if (typeof val === "boolean") {
      q.append(key, val ? "true" : "false");
      return;
    }

    // Autres types
    const str = String(val).trim();
    if (str !== "") q.append(key, str);
  });

  const qs = q.toString();
  return qs ? `?${qs}` : "";
};

// URL absolue pour les photos (utile si tu stockes en /uploads)
export const toAbsolutePhotoUrl = (url) => {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;

  const base = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
  if (url.startsWith("/uploads/") || url.startsWith("uploads/")) {
    return `${base}${url.startsWith("/") ? "" : "/"}${url}`;
  }
  return url;
};

/* =========================
   API — Public
   ========================= */

/**
 * Liste publique du trombinoscope
 * params possibles :
 *   - q
 *   - role_type
 *   - is_elected
 *   - groupe
 *   - service_id
 *   - sort ("ordre","nom","prenom","created_at")
 *   - dir ("ASC","DESC")
 */
export const getPublicTrombinos = async (params = {}) => {
  try {
    const qs = buildQuery(params);
    const { data } = await axios.get(`${baseUrl}/public${qs}`);
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error("Erreur chargement trombinoscope public :", error);
    return [];
  }
};

/**
 * Liste publique filtrée par service
 */
export const getPublicTrombinosByService = async (
  serviceId,
  params = {}
) => {
  if (!serviceId) return [];
  try {
    const qs = buildQuery(params);
    const { data } = await axios.get(
      `${baseUrl}/byService/${serviceId}${qs}`
    );
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error(
      `Erreur chargement trombinoscope pour le service ${serviceId} :`,
      error
    );
    return [];
  }
};

/**
 * Détail d’une personne du trombinoscope
 */
export const getTrombinoById = async (id) => {
  if (!id) return null;
  try {
    const { data } = await axios.get(`${baseUrl}/${id}`);
    return data || null;
  } catch (error) {
    console.error("Erreur chargement entrée trombinoscope :", error);
    return null;
  }
};

/* =========================
   API — Admin (search + CRUD)
   ========================= */

/**
 * Recherche / filtres / pagination (admin)
 *
 * params:
 *  - q
 *  - role_type (elu, personnel, autre…)
 *  - is_elected
 *  - is_visible
 *  - service_id
 *  - groupe
 *  - page
 *  - limit
 *  - sort ("ordre","nom","prenom","created_at","role_type","groupe")
 *  - dir ("ASC"|"DESC")
 */
export const searchTrombinos = async (params = {}) => {
  try {
    const qs = buildQuery(params);
    const { data } = await axios.get(`${baseUrl}/search${qs}`, {
      headers: authHeaders(),
    });

    return {
      items: Array.isArray(data?.items) ? data.items : [],
      total: Number.isFinite(data?.total) ? data.total : 0,
      page: Number.isFinite(data?.page) ? data.page : 1,
      limit: Number.isFinite(data?.limit) ? data.limit : 24,
      totalPages: Number.isFinite(data?.totalPages) ? data.totalPages : 1,
    };
  } catch (error) {
    console.error("Erreur recherche trombinoscope (admin) :", error);
    return { items: [], total: 0, page: 1, limit: 24, totalPages: 1 };
  }
};

/**
 * Création d’une entrée de trombinoscope
 */
export const createTrombino = async (payload) => {
  try {
    const { data } = await axios.post(`${baseUrl}`, payload, {
      headers: {
        "Content-Type": "application/json",
        ...authHeaders(),
      },
    });
    return data || null;
  } catch (error) {
    console.error("Erreur création entrée trombinoscope :", error);
    return null;
  }
};

/**
 * Remplacement complet (PUT)
 */
export const updateTrombino = async (id, payload) => {
  if (!id) return null;
  try {
    const { data } = await axios.put(`${baseUrl}/${id}`, payload, {
      headers: {
        "Content-Type": "application/json",
        ...authHeaders(),
      },
    });
    return data || null;
  } catch (error) {
    console.error("Erreur mise à jour entrée trombinoscope :", error);
    return null;
  }
};

/**
 * Mise à jour partielle (PATCH)
 */
export const patchTrombino = async (id, partial) => {
  if (!id) return null;
  try {
    const { data } = await axios.patch(`${baseUrl}/${id}`, partial, {
      headers: {
        "Content-Type": "application/json",
        ...authHeaders(),
      },
    });
    return data || null;
  } catch (error) {
    console.error("Erreur patch entrée trombinoscope :", error);
    return null;
  }
};

/**
 * Toggle visibilité publique
 */
export const toggleTrombinoVisible = async (id) => {
  if (!id) return null;
  try {
    const { data } = await axios.patch(
      `${baseUrl}/${id}/toggle-visible`,
      null,
      { headers: authHeaders() }
    );
    return data || null;
  } catch (error) {
    console.error("Erreur toggle visibilité trombinoscope :", error);
    return null;
  }
};

/**
 * Toggle statut élu / personnel
 */
export const toggleTrombinoElected = async (id) => {
  if (!id) return null;
  try {
    const { data } = await axios.patch(
      `${baseUrl}/${id}/toggle-elected`,
      null,
      { headers: authHeaders() }
    );
    return data || null;
  } catch (error) {
    console.error("Erreur toggle élu trombinoscope :", error);
    return null;
  }
};

/**
 * Suppression
 */
export const deleteTrombino = async (id) => {
  if (!id) return false;
  try {
    await axios.delete(`${baseUrl}/${id}`, {
      headers: authHeaders(),
    });
    return true;
  } catch (error) {
    console.error("Erreur suppression entrée trombinoscope :", error);
    return false;
  }
};
